// One Lambda response can carry only ~4 MB of image (6 MB cap, base64). A bigger image is fetched from
// the proxy as ?offset=&length= slices (S3 range reads) and joined here, so images up to the 10 MB admin
// limit work through the API without any bucket setting.
const PIECE_BYTES = 3 * 1024 * 1024;
const MAX_PIECES = 8;

async function fetchProxyInPieces({ fetchImpl, backendUrl, asset, token, size }) {
  const total = Number(size);
  if (!(total > 0) || Math.ceil(total / PIECE_BYTES) > MAX_PIECES) return null;
  const count = Math.ceil(total / PIECE_BYTES);
  try {
    const parts = await Promise.all(
      Array.from({ length: count }, async (_, i) => {
        const offset = i * PIECE_BYTES;
        const length = Math.min(PIECE_BYTES, total - offset);
        const res = await fetchImpl(
          `${backendUrl}/themeAsset?asset=${encodeURIComponent(asset)}&offset=${offset}&length=${length}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`piece ${i + 1}/${count} failed (${res.status})`);
        return { blob: await res.blob(), type: res.headers && res.headers.get && res.headers.get('content-type') };
      })
    );
    const type = parts[0].type || undefined;
    return URL.createObjectURL(new Blob(parts.map((p) => p.blob), type ? { type } : undefined));
  } catch {
    return null;
  }
}

function isJson(res) {
  const type = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
  return type.includes('application/json');
}

async function readJson(res) {
  const type = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
  return type.includes('application/json') ? res.json().catch(() => ({})) : {};
}

/**
 * Images are read through the API's /themeAsset proxy by default. Direct reads from S3 are opt-in
 * (REACT_APP_ASSET_DIRECT=true) because they only work once the bucket allows public reads and
 * this site's CORS; until then every image produced a red 403 before the proxy answered.
 */
export function isDirectLoadingEnabled() {
  return process.env.REACT_APP_ASSET_DIRECT === 'true';
}

/**
 * The URL used for the cross-origin read. If the same image was already shown in an <img> tag, the
 * browser may hold a cached copy WITHOUT the CORS headers, and reusing it makes the CORS read fail
 * (a well-known S3 + browser-cache trap). A throwaway query parameter gives the CORS read its own
 * cache entry; S3 ignores it, and the names never change so it still caches normally.
 */
export function directUrl(url) {
  return `${url}${String(url).includes('?') ? '&' : '?'}cors=1`;
}

/**
 * Loads a theme/puzzle image and returns an object URL the canvas can draw on without being
 * "tainted" (which would block exporting the artwork).
 *
 * Two ways to get the bytes:
 *  1. DIRECT from S3/CloudFront - no Lambda in the path. Needs a CORS rule on the bucket that
 *     allows this app's origin (see backend/scripts/setBucketCors.js). OFF by default; set
 *     REACT_APP_ASSET_DIRECT=true to turn it on.
 *  2. Through the backend's /themeAsset proxy (the default): same-origin, so it always works,
 *     but every player's image request becomes a Lambda invocation that buffers the whole
 *     object - 500 players entering the canvas together means 500 of them.
 *
 * When direct loading is on and fails (for example CORS is not configured yet) the proxy is
 * used, so turning the flag on can never break the page. If neither works the original URL is
 * returned (best effort, as before).
 */
export async function fetchAssetAsObjectUrl(assetUrl, token, options = {}) {
  if (!assetUrl) return '';
  const {
    backendUrl = process.env.REACT_APP_BACKEND_URL,
    direct = isDirectLoadingEnabled(),
    fetchImpl = (...args) => fetch(...args),
  } = options;

  if (direct && /^https?:\/\//i.test(assetUrl)) {
    try {
      const res = await fetchImpl(directUrl(assetUrl), { mode: 'cors', credentials: 'omit' });
      if (res.ok) return URL.createObjectURL(await res.blob());
    } catch {
      /* CORS not configured or network error: use the proxy below */
    }
  }

  if (!backendUrl || !token) return assetUrl;
  try {
    // probe=1: for an image too big for one response the server answers an ordinary 200 with its size
    // (no red 413 in the console), and the image is then fetched in slices.
    const res = await fetchImpl(`${backendUrl}/themeAsset?asset=${encodeURIComponent(assetUrl)}&probe=1`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok && !isJson(res)) return URL.createObjectURL(await res.blob());
    if (res.ok || res.status === 413) {
      const body = await readJson(res);
      if (body.code === 'ASSET_TOO_LARGE') {
        const joined = await fetchProxyInPieces({ fetchImpl, backendUrl, asset: assetUrl, token, size: body.size });
        if (joined) return joined;
      }
    }
    return assetUrl;
  } catch {
    return assetUrl;
  }
}

export const IMAGE_TOO_LARGE_FOR_API_MESSAGE =
  'This image is too large to load through the API and could not be loaded directly from storage either. ' +
  "Allow this site in the S3 bucket's CORS settings so large images can load directly, or re-upload a smaller image.";

/**
 * Loads a saved theme image for the ADMIN editor (slicing it into puzzle pieces needs the bytes) and
 * returns an object URL, or THROWS an Error with a message an admin can act on.
 *
 * Order: direct from S3 (default) -> the /themeAsset proxy (slices for big images).
 * One proxy response cannot carry more than ~4 MB (Lambda's response limit). For a bigger image the new
 * backend answers 413 with the image size and the image is then fetched in slices and joined. An old
 * backend answers with a bare 500 that has no CORS headers (the browser reports a misleading "CORS
 * error" / "Failed to fetch"); that case falls through to the direct load, which works once the
 * bucket's CORS rule allows this site.
 *
 * `filename` is the bare file name; `publicUrl` is its absolute S3/CloudFront URL.
 */
export async function loadThemeImage(filename, token, options = {}) {
  const {
    publicUrl = '',
    backendUrl = process.env.REACT_APP_BACKEND_URL,
    direct = isDirectLoadingEnabled(),
    fetchImpl = (...args) => fetch(...args),
  } = options;

  const tryDirect = async () => {
    if (!/^https?:\/\//i.test(publicUrl || '')) return null;
    try {
      const res = await fetchImpl(directUrl(publicUrl), { mode: 'cors', credentials: 'omit' });
      if (res.ok) return URL.createObjectURL(await res.blob());
    } catch {
      /* bucket CORS not configured for this origin */
    }
    return null;
  };

  let directTried = false;
  if (direct) {
    directTried = true;
    const url = await tryDirect();
    if (url) return url;
  }

  let proxyFailure = null;
  if (backendUrl && token && filename) {
    try {
      const res = await fetchImpl(`${backendUrl}/themeAsset?asset=${encodeURIComponent(filename)}&probe=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok && !isJson(res)) return URL.createObjectURL(await res.blob());
      const body = await readJson(res);
      proxyFailure = { status: res.status, code: body.code, message: body.message, size: body.size };
      if (body.code === 'ASSET_TOO_LARGE') {
        const joined = await fetchProxyInPieces({ fetchImpl, backendUrl, asset: filename, token, size: body.size });
        if (joined) return joined;
      }
    } catch (err) {
      proxyFailure = { network: true, message: err && err.message };
    }
  }

  if (!directTried) {
    const url = await tryDirect();
    if (url) return url;
  }

  if (proxyFailure && (proxyFailure.code === 'ASSET_TOO_LARGE' || proxyFailure.network || proxyFailure.status >= 500)) {
    throw new Error(IMAGE_TOO_LARGE_FOR_API_MESSAGE);
  }
  throw new Error((proxyFailure && proxyFailure.message) || 'Failed to load puzzle image.');
}
